import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Plus, Pencil, Trash2, ImagePlus, Filter, Search } from "lucide-react";
import { toast } from "sonner";
import { services } from "@/lib/services-data";

type Review = {
  id: string;
  name: string;
  location: string | null;
  rating: number;
  treatment: string | null;
  review: string;
  before_image_url: string | null;
  after_image_url: string | null;
  is_published: boolean;
  created_at: string;
};

const emptyForm = { name: "", rating: 5, treatment: "", review: "", before_image_url: "", after_image_url: "", is_published: false, add_to_gallery: false };

const AdminReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchReviews = async () => {
    const { data } = await supabase.from("reviews").select("*").order("created_at", { ascending: false });
    if (data) setReviews(data);
  };

  useEffect(() => { fetchReviews(); }, []);

  const uploadImage = async (file: File, field: "before_image_url" | "after_image_url") => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `reviews/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("admin-uploads").upload(path, file);
    if (error) { toast.error("Upload failed"); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from("admin-uploads").getPublicUrl(path);
    setForm({ ...form, [field]: publicUrl });
    setUploading(false);
  };

  const addToGallery = async (treatment: string, beforeUrl: string, afterUrl: string) => {
    const svc = services.find(s => s.title === treatment);
    const category = svc?.title || "General";
    const { error } = await supabase.from("gallery_items").insert({
      treatment,
      category,
      before_image_url: beforeUrl,
      after_image_url: afterUrl,
      is_published: true,
    });
    if (error) {
      toast.error("Failed to add to gallery");
    } else {
      toast.success("Added to gallery");
    }
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      location: null,
      rating: form.rating,
      treatment: form.treatment || null,
      review: form.review,
      before_image_url: form.before_image_url || null,
      after_image_url: form.after_image_url || null,
      is_published: form.is_published,
    };

    if (editId) {
      await supabase.from("reviews").update(payload).eq("id", editId);
      toast.success("Review updated");
    } else {
      await supabase.from("reviews").insert(payload);
      toast.success("Review created");
    }

    if (form.add_to_gallery && form.before_image_url && form.after_image_url && form.treatment) {
      await addToGallery(form.treatment, form.before_image_url, form.after_image_url);
    }

    setOpen(false);
    setForm(emptyForm);
    setEditId(null);
    fetchReviews();
  };

  const handleEdit = (r: Review) => {
    setForm({
      name: r.name,
      rating: r.rating,
      treatment: r.treatment || "",
      review: r.review,
      before_image_url: r.before_image_url || "",
      after_image_url: r.after_image_url || "",
      is_published: r.is_published,
      add_to_gallery: false,
    });
    setEditId(r.id);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this review?")) return;
    await supabase.from("reviews").delete().eq("id", id);
    toast.success("Review deleted");
    fetchReviews();
  };

  const togglePublish = async (id: string, current: boolean) => {
    await supabase.from("reviews").update({ is_published: !current }).eq("id", id);
    fetchReviews();
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Hero */}
      <section className="admin-gradient-hero rounded-3xl px-6 lg:px-10 py-8 lg:py-10">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-semibold">Review Intelligence</h1>
            <p className="text-white/70 mt-2 text-sm lg:text-base max-w-lg">Real-time patient feedback monitoring and curation.</p>
          </div>
          <div className="flex gap-3">
            <div className="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60">Total Reviews</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-white">{reviews.length}</p>
            </div>
            <div className="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/60">Published</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-white">{reviews.filter(r => r.is_published).length}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Action bar */}
      <div className="admin-card p-4 lg:p-5 flex items-center justify-between gap-3">
        <p className="admin-eyebrow flex items-center gap-2"><Filter className="h-3.5 w-3.5" /> Intelligence Matrix</p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm); setEditId(null); } }}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: "linear-gradient(135deg, hsl(var(--admin-teal)), hsl(var(--admin-navy)))" }}>
              <Plus className="h-4 w-4" /> New Review
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Edit Review" : "Add Review"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Patient Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[5,4,3,2,1].map(r => <SelectItem key={r} value={String(r)}>{r} Star{r > 1 ? "s" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={form.treatment} onValueChange={(v) => setForm({ ...form, treatment: v })}>
                <SelectTrigger><SelectValue placeholder="Select Treatment" /></SelectTrigger>
                <SelectContent>
                  {services.map(s => <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea placeholder="Review text" value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} rows={3} />

              <div>
                <label className="text-xs font-medium mb-1 block">Before Image</label>
                {form.before_image_url && <img src={form.before_image_url} alt="Before" className="h-24 rounded mb-1 object-cover" />}
                <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "before_image_url")} disabled={uploading} />
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block">After Image</label>
                {form.after_image_url && <img src={form.after_image_url} alt="After" className="h-24 rounded mb-1 object-cover" />}
                <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "after_image_url")} disabled={uploading} />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
                <span className="text-sm">Published</span>
              </div>

              {form.before_image_url && form.after_image_url && (
                <div className="flex items-center gap-2">
                  <Switch checked={form.add_to_gallery} onCheckedChange={(v) => setForm({ ...form, add_to_gallery: v })} />
                  <span className="text-sm">Also add to Gallery</span>
                </div>
              )}

              <Button onClick={handleSave} className="w-full" disabled={!form.name || !form.review}>
                {editId ? "Update" : "Create"} Review
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="admin-card p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1.5">
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold uppercase shrink-0" style={{ background: "linear-gradient(135deg, hsl(var(--admin-teal)), hsl(var(--admin-navy)))" }}>
                  {r.name.slice(0,1)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{r.name}</p>
                    <div className="flex gap-0.5">
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-[hsl(var(--admin-gold))] text-[hsl(var(--admin-gold))]" />
                      ))}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${r.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {r.is_published ? "Published" : "Draft"}
                    </span>
                  </div>
                  {r.treatment && <p className="text-xs text-[hsl(var(--admin-muted))] mt-0.5">{r.treatment}</p>}
                </div>
              </div>
              <p className="text-sm mt-2 line-clamp-2 text-[hsl(var(--admin-ink))]/85">{r.review}</p>
              {(r.before_image_url || r.after_image_url) && (
                <div className="flex gap-2 mt-3">
                  {r.before_image_url && <img src={r.before_image_url} alt="Before" className="h-16 w-20 object-cover rounded-lg" />}
                  {r.after_image_url && <img src={r.after_image_url} alt="After" className="h-16 w-20 object-cover rounded-lg" />}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Switch checked={r.is_published} onCheckedChange={() => togglePublish(r.id, r.is_published)} />
              {r.before_image_url && r.after_image_url && (
                <Button size="icon" variant="ghost" aria-label="Add to gallery" title="Add to Gallery" onClick={() => addToGallery(r.treatment || "General", r.before_image_url!, r.after_image_url!)}>
                  <ImagePlus className="h-4 w-4 text-[hsl(var(--admin-teal-deep))]" />
                </Button>
              )}
              <Button size="icon" variant="ghost" aria-label="Edit review" onClick={() => handleEdit(r)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" aria-label="Delete review" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-[hsl(var(--admin-muted))] text-center py-12 admin-card">No reviews yet. Add your first one.</p>}
      </div>
    </div>
  );
};

export default AdminReviews;
